import { useId } from 'react';
import { motion, type TargetAndTransition, type Variants } from 'framer-motion';
import { EASE_EXPO, EASE_SOFT } from '@/lib/motion';
import {
  GOVERNED_CASE_PHASE_ORDER,
  type GovernedCasePhase,
} from './governed-case-state';

export interface GovernedCaseRailProps {
  phase: GovernedCasePhase;
  className?: string;
  variant?: 'case-rail' | 'investor-case';
}

/*
 * A deliberately literal, text-free product story: email, meetings and
 * paperwork resolve through the real Asset Hound mark, stop at a distinct
 * human gate, then become ordered case files only after release.
 *
 * Every animated element has an absolute target for all five phases, keeping
 * direct entry into `held` deterministic for reduced-motion and save-data
 * visitors.
 */

const INK = 'var(--v5-ink, #f3f5f7)';
const MUTED = 'var(--v5-muted, #52647c)';
const BLUE = 'var(--v5-blue, #2f6bff)';
const BLUE_BRIGHT = 'var(--v5-blue-bright, #62d0ff)';
const AMBER = 'var(--v5-amber, #f5a524)';
const PANEL = 'var(--v5-panel, #0d1420)';

const FADE = { duration: 0.4, ease: 'easeOut' } as const;
const DRAW = { duration: 0.72, ease: EASE_EXPO } as const;
const SETTLE = { duration: 0.62, ease: EASE_SOFT } as const;

const SELF_CENTERED = {
  transformBox: 'fill-box',
  transformOrigin: '50% 50%',
} as const;

function fromPhase(
  activeFrom: GovernedCasePhase,
  before: TargetAndTransition,
  after: TargetAndTransition,
): Variants {
  const threshold = GOVERNED_CASE_PHASE_ORDER.indexOf(activeFrom);
  const variants: Variants = {};
  GOVERNED_CASE_PHASE_ORDER.forEach((phase, index) => {
    variants[phase] = index >= threshold ? after : before;
  });
  return variants;
}

function fragmentPose(x: number, y: number, rotate: number): Variants {
  return fromPhase(
    'aligned',
    { x, y, rotate, opacity: 0.72, transition: SETTLE },
    { x: 0, y: 0, rotate: 0, opacity: 1, transition: SETTLE },
  );
}

function inputConnector(index: number): Variants {
  return fromPhase(
    'aligned',
    { pathLength: 0, opacity: 0 },
    {
      pathLength: 1,
      opacity: 0.86,
      transition: { ...DRAW, delay: index * 0.08 },
    },
  );
}

const brandFrame: Variants = {
  fragmented: { opacity: 0.28, scale: 0.92 },
  aligned: { opacity: 0.68, scale: 1, transition: SETTLE },
  rail: { opacity: 1, scale: 1, transition: FADE },
  held: { opacity: 1, scale: 1 },
  released: { opacity: 1, scale: 1 },
};

const brandAura: Variants = {
  fragmented: { opacity: 0.02, scale: 0.92 },
  aligned: { opacity: 0.08, scale: 0.96, transition: SETTLE },
  rail: { opacity: 0.32, scale: 0.99, transition: SETTLE },
  held: { opacity: 0.5, scale: 1, transition: FADE },
  released: { opacity: 0.58, scale: 1, transition: FADE },
};

const brandMark: Variants = {
  fragmented: { opacity: 0.5, scale: 0.94 },
  aligned: { opacity: 0.82, scale: 1, transition: SETTLE },
  rail: { opacity: 1, scale: 1, transition: FADE },
  held: { opacity: 1, scale: 1 },
  released: { opacity: 1, scale: 1 },
};

const governedRail = fromPhase(
  'rail',
  { pathLength: 0, opacity: 0 },
  { pathLength: 1, opacity: 1, transition: DRAW },
);

const gateRing: Variants = {
  fragmented: { opacity: 0.14 },
  aligned: { opacity: 0.24, transition: FADE },
  rail: { opacity: 0.72, transition: FADE },
  held: { opacity: 1, transition: FADE },
  released: { opacity: 0.38, transition: FADE },
};

const closedBarrier: Variants = {
  fragmented: { opacity: 0 },
  aligned: { opacity: 0 },
  rail: { opacity: 0.5, transition: FADE },
  held: { opacity: 1, transition: FADE },
  released: { opacity: 0, transition: FADE },
};

const openBarrier = fromPhase(
  'released',
  { pathLength: 0, opacity: 0 },
  {
    pathLength: 1,
    opacity: 1,
    transition: { duration: 0.42, ease: EASE_EXPO },
  },
);

const outputDistributor = fromPhase(
  'released',
  { pathLength: 0, opacity: 0 },
  {
    pathLength: 1,
    opacity: 1,
    transition: { duration: 0.58, ease: EASE_EXPO, delay: 0.08 },
  },
);

function proofCard(index: number): Variants {
  return {
    fragmented: { opacity: 0, x: 8 },
    aligned: { opacity: 0, x: 8 },
    rail: { opacity: 0, x: 8 },
    held: { opacity: 0.18, x: 4, transition: FADE },
    released: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.46,
        ease: EASE_EXPO,
        delay: 0.18 + index * 0.14,
      },
    },
  };
}

const OUTPUT_CASE_Y = [54, 118, 182];

export function GovernedCaseRail({
  phase,
  className,
  variant = 'case-rail',
}: GovernedCaseRailProps): JSX.Element {
  const released = phase === 'released';
  const instanceId = useId().replace(/:/g, '');
  const glowGradientId = `v5-brand-glow-${instanceId}`;
  const titleId = `investor-case-title-${instanceId}`;
  const descriptionId = `investor-case-description-${instanceId}`;
  const investorCase = variant === 'investor-case';

  return (
    <svg
      viewBox="0 0 520 280"
      className={className}
      style={{ display: 'block', width: '100%', height: 'auto' }}
      aria-hidden={investorCase ? undefined : true}
      aria-labelledby={investorCase ? `${titleId} ${descriptionId}` : undefined}
      focusable="false"
      role={investorCase ? 'img' : 'presentation'}
      data-phase={phase}
      data-v5-governed-case-rail=""
    >
      {investorCase ? (
        <>
          <title id={titleId}>Asset Hound legislation-to-case flow</title>
          <desc id={descriptionId}>
            Disordered emails, meetings, documents, the Insolvency Act 1986 and
            the Insolvency Rules 2016 resolve through Asset Hound into one
            governed case view.
          </desc>
        </>
      ) : null}
      <defs>
        <radialGradient id={glowGradientId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#62d0ff" stopOpacity={0.22} />
          <stop offset="52%" stopColor="#2f6bff" stopOpacity={0.11} />
          <stop offset="100%" stopColor="#2f6bff" stopOpacity={0} />
        </radialGradient>
      </defs>
      <motion.g initial={false} animate={phase}>
        <g data-v5-layer="fragments">
          <motion.g
            data-v5-input="email"
            variants={fragmentPose(-9, -12, -7)}
            style={SELF_CENTERED}
          >
            {!investorCase ? (
              <rect
                x={22}
                y={54}
                width={102}
                height={48}
                rx={4}
                style={{ fill: PANEL, stroke: MUTED }}
                strokeWidth={1.5}
              />
            ) : (
              <InvestorInputPort input="email" y={78} />
            )}
            <path
              d="M34 64H88V92H34Z"
              fill="none"
              style={{ stroke: investorCase ? BLUE_BRIGHT : MUTED }}
              strokeOpacity={investorCase ? 0.76 : 0.88}
              strokeWidth={1.8}
              strokeLinejoin="round"
            />
            <path
              d="M36 66L61 82L86 66"
              fill="none"
              style={{ stroke: BLUE_BRIGHT }}
              strokeOpacity={0.94}
              strokeWidth={1.8}
              strokeLinejoin="round"
            />
            {!investorCase ? (
              <path
                d="M96 70H112M96 80H108M96 90H113"
                style={{ stroke: INK }}
                strokeOpacity={0.38}
                strokeWidth={2.4}
                strokeLinecap="round"
              />
            ) : null}
          </motion.g>

          <motion.g
            data-v5-input="meeting"
            variants={fragmentPose(15, 4, 5)}
            style={SELF_CENTERED}
          >
            {!investorCase ? (
              <rect
                x={22}
                y={118}
                width={102}
                height={48}
                rx={4}
                style={{ fill: PANEL, stroke: MUTED }}
                strokeWidth={1.5}
              />
            ) : (
              <InvestorInputPort input="meeting" y={142} />
            )}
            <rect
              data-v5-meeting-calendar=""
              x={34}
              y={124}
              width={56}
              height={34}
              rx={4}
              fill="none"
              style={{ stroke: investorCase ? BLUE_BRIGHT : MUTED }}
              strokeOpacity={investorCase ? 0.76 : 0.82}
              strokeWidth={1.8}
            />
            <path
              d="M48 120V127M76 120V127"
              fill="none"
              style={{ stroke: BLUE_BRIGHT }}
              strokeOpacity={0.88}
              strokeWidth={2}
              strokeLinecap="round"
            />
            <path
              d="M34 132H90"
              fill="none"
              style={{ stroke: investorCase ? BLUE_BRIGHT : MUTED }}
              strokeOpacity={investorCase ? 0.6 : 0.7}
              strokeWidth={1.5}
            />
            <rect
              x={43}
              y={139}
              width={18}
              height={10}
              rx={2}
              style={{ fill: BLUE_BRIGHT }}
              fillOpacity={0.78}
            />
            {!investorCase ? (
              <path
                d="M100 132H113M100 142H109M100 152H114"
                style={{ stroke: INK }}
                strokeOpacity={0.36}
                strokeWidth={2.2}
                strokeLinecap="round"
              />
            ) : null}
          </motion.g>

          <motion.g
            data-v5-input="paperwork"
            variants={fragmentPose(-5, 13, -5)}
            style={SELF_CENTERED}
          >
            {!investorCase ? (
              <rect
                x={22}
                y={182}
                width={102}
                height={48}
                rx={4}
                style={{ fill: PANEL, stroke: MUTED }}
                strokeWidth={1.5}
              />
            ) : (
              <InvestorInputPort input="casework" y={206} />
            )}
            <path
              d="M42 188H72L86 202V228H42Z"
              fill="none"
              style={{ stroke: investorCase ? BLUE_BRIGHT : MUTED }}
              strokeOpacity={investorCase ? 0.76 : 0.9}
              strokeWidth={1.8}
              strokeLinejoin="round"
            />
            <path
              d="M72 188V202H86"
              fill="none"
              style={{ stroke: BLUE_BRIGHT }}
              strokeOpacity={0.9}
              strokeWidth={1.8}
              strokeLinejoin="round"
            />
            <path
              d="M51 209H76M51 217H69"
              fill="none"
              style={{ stroke: INK }}
              strokeOpacity={0.36}
              strokeWidth={2.2}
              strokeLinecap="round"
            />
            {!investorCase ? (
              <path
                d="M91 198H112M91 208H107M91 218H113"
                style={{ stroke: INK }}
                strokeOpacity={0.36}
                strokeWidth={2.2}
                strokeLinecap="round"
              />
            ) : null}
          </motion.g>
          {investorCase ? <InvestorCaseInputs /> : null}
        </g>

        <g data-v5-layer="alignment">
          <motion.path
            data-v5-input-branch="email"
            variants={inputConnector(0)}
            d="M124 78C158 78 168 103 194 118"
            fill="none"
            style={{ stroke: investorCase ? BLUE_BRIGHT : BLUE }}
            strokeOpacity={investorCase ? 0.74 : 1}
            strokeWidth={investorCase ? 2.8 : 2.4}
            strokeLinecap="round"
          />
          <motion.path
            data-v5-input-branch="meeting"
            variants={inputConnector(1)}
            d="M124 142H191"
            fill="none"
            style={{ stroke: investorCase ? BLUE_BRIGHT : BLUE }}
            strokeOpacity={investorCase ? 0.74 : 1}
            strokeWidth={investorCase ? 2.8 : 2.4}
            strokeLinecap="round"
          />
          <motion.path
            data-v5-input-branch="casework"
            variants={inputConnector(2)}
            d="M124 206C158 206 168 177 194 162"
            fill="none"
            style={{ stroke: investorCase ? BLUE_BRIGHT : BLUE }}
            strokeOpacity={investorCase ? 0.74 : 1}
            strokeWidth={investorCase ? 2.8 : 2.4}
            strokeLinecap="round"
          />
        </g>

        <g data-v5-layer="rail">
          <motion.g
            data-v5-brand-glow=""
            variants={brandAura}
            style={SELF_CENTERED}
          >
            <circle
              className="pitch-v5-brand-aura pitch-v5-brand-aura--outer"
              cx={244}
              cy={140}
              r={68}
              fill={`url(#${glowGradientId})`}
            />
          </motion.g>
          <motion.g variants={brandFrame} style={SELF_CENTERED}>
            <circle
              cx={244}
              cy={140}
              r={53}
              style={{ fill: PANEL, stroke: BLUE_BRIGHT }}
              strokeOpacity={0.34}
              strokeWidth={1.5}
            />
            <circle
              cx={244}
              cy={140}
              r={46}
              fill="none"
              style={{ stroke: BLUE_BRIGHT }}
              strokeOpacity={0.72}
              strokeWidth={2}
            />
          </motion.g>
          <motion.image
            data-v5-brand-lens
            className="pitch-v5-brand-mark"
            variants={brandMark}
            href="/brand/asset-hound-icon.png"
            x={196}
            y={92}
            width={96}
            height={96}
            preserveAspectRatio="xMidYMid meet"
            style={SELF_CENTERED}
          />
          <motion.path
            variants={governedRail}
            d="M297 140H328"
            fill="none"
            style={{ stroke: BLUE_BRIGHT }}
            strokeWidth={6}
            strokeLinecap="round"
          />
        </g>

        <g data-v5-layer="gate" data-open={released ? 'true' : 'false'}>
          <motion.circle
            variants={gateRing}
            cx={356}
            cy={140}
            r={28}
            style={{ fill: PANEL, stroke: AMBER }}
            strokeWidth={2.5}
          />
          {investorCase ? (
            <motion.circle
              data-v5-approval-gate-core=""
              variants={gateRing}
              cx={356}
              cy={140}
              r={20}
              style={{ fill: AMBER }}
              fillOpacity={0.055}
              stroke="none"
            />
          ) : null}
          <motion.g
            data-v5-gate-barrier
            data-v5-gate-review="human"
            variants={closedBarrier}
          >
            <circle
              cx={351}
              cy={134}
              r={4.5}
              style={{ fill: AMBER }}
              fillOpacity={0.94}
            />
            <path
              d="M342 151C343 142 359 142 360 151M362 143L366 147L373 139"
              fill="none"
              style={{ stroke: AMBER }}
              strokeWidth={2.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </motion.g>
          <motion.path
            variants={openBarrier}
            d="M338 140H374"
            fill="none"
            style={{ stroke: BLUE_BRIGHT }}
            strokeWidth={6}
            strokeLinecap="round"
          />
        </g>

        {investorCase ? <InvestorCaseConnectors /> : null}

        {investorCase ? (
          <InvestorOutputCase />
        ) : (
          <g data-v5-layer="outputs" data-active={released ? 'true' : 'false'}>
            <motion.path
              variants={outputDistributor}
              d="M384 140H400M400 78V206M400 78H410M400 142H410M400 206H410"
              fill="none"
              style={{ stroke: BLUE_BRIGHT }}
              strokeWidth={3}
              strokeLinecap="round"
            />
            {OUTPUT_CASE_Y.map((y, index) => (
              <motion.g
                key={y}
                data-v5-role="case-file"
                variants={proofCard(index)}
              >
                <path
                  data-v5-case-folder=""
                  d={`M411 ${y + 10}H439L446 ${y + 16}H501V${y + 46}H411Z`}
                  style={{ fill: PANEL, stroke: BLUE_BRIGHT }}
                  strokeOpacity={0.74}
                  strokeWidth={1.5}
                  strokeLinejoin="round"
                />
                <path
                  d={`M425 ${y + 25}H486M425 ${y + 33}H478M425 ${y + 40}H466`}
                  style={{ stroke: INK }}
                  strokeOpacity={0.38}
                  strokeWidth={2.4}
                  strokeLinecap="round"
                />
                <circle
                  cx={489}
                  cy={y + 39}
                  r={3}
                  style={{ fill: BLUE_BRIGHT }}
                  fillOpacity={0.72}
                />
              </motion.g>
            ))}
          </g>
        )}
      </motion.g>
    </svg>
  );
}

function InvestorInputPort({
  input,
  y,
}: {
  input: 'email' | 'meeting' | 'casework';
  y: number;
}) {
  return (
    <g data-v5-input-port-group={input}>
      <path
        d={`M100 ${y}H124`}
        fill="none"
        stroke={BLUE_BRIGHT}
        strokeOpacity={0.54}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <circle
        data-v5-input-port={input}
        cx={124}
        cy={y}
        r={3.8}
        fill={PANEL}
        stroke={BLUE_BRIGHT}
        strokeWidth={1.4}
      />
      <circle cx={124} cy={y} r={1.35} fill={BLUE_BRIGHT} />
    </g>
  );
}

function InvestorCaseInputs() {
  return (
    <g data-v5-layer="legislation-inputs">
      <motion.g
        data-v5-input="insolvency-act"
        variants={fragmentPose(-8, -8, -5)}
        style={SELF_CENTERED}
      >
        <InvestorLegislationDossier
          x={264}
          y={5}
          width={184}
          height={43}
          titleLines={['INSOLVENCY ACT']}
          year="1986"
        />
      </motion.g>
      <motion.g
        data-v5-input="insolvency-rules"
        variants={fragmentPose(10, 9, 5)}
        style={SELF_CENTERED}
      >
        <InvestorLegislationDossier
          x={264}
          y={232}
          width={184}
          height={43}
          titleLines={['INSOLVENCY RULES']}
          year="2016"
        />
      </motion.g>
    </g>
  );
}

function InvestorLegislationDossier({
  x,
  y,
  width,
  height,
  titleLines,
  year,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  titleLines: readonly string[];
  year: string;
}) {
  const textX = 14;
  const hasSecondLine = titleLines.length > 1;

  return (
    <g
      transform={`translate(${x} ${y})`}
      data-v5-legislation-dossier=""
      data-v5-legislation-year={year}
    >
      <path
        d={`M1 5V${height - 5}M1 5H${width - 1}M1 ${height - 5}H${width - 1}`}
        fill="none"
        stroke={AMBER}
        strokeOpacity={0.44}
        strokeWidth={1.2}
        strokeLinecap="round"
      />
      <circle cx={1} cy={height / 2} r={3.2} fill={PANEL} stroke={AMBER} />
      <circle cx={1} cy={height / 2} r={1.15} fill={AMBER} />

      <text
        x={textX}
        y={hasSecondLine ? 12 : 13}
        fill={AMBER}
        fontSize={6.5}
        fontWeight={700}
        letterSpacing="0.18em"
      >
        LEGISLATION
      </text>
      <text
        x={textX}
        y={hasSecondLine ? 24 : 29}
        fill={INK}
        fontSize={hasSecondLine ? 8.5 : 10.8}
        fontWeight={750}
        letterSpacing={hasSecondLine ? '0.02em' : '0.035em'}
      >
        {titleLines[0]}
      </text>
      {hasSecondLine ? (
        <text
          x={textX}
          y={34}
          fill={INK}
          fontSize={8.1}
          fontWeight={700}
          letterSpacing="0.025em"
        >
          {titleLines[1]}
        </text>
      ) : null}
      <text
        data-v5-legislation-year-label={year}
        x={width - 10}
        y={hasSecondLine ? 34 : 29}
        fill={BLUE_BRIGHT}
        fontSize={hasSecondLine ? 8.2 : 9.6}
        fontWeight={750}
        letterSpacing="0.08em"
        textAnchor="end"
      >
        {year}
      </text>
    </g>
  );
}

function InvestorCaseConnectors() {
  return (
    <g data-v5-layer="legislation-to-approval-gate">
      <motion.path
        data-v5-legislation-connector="insolvency-act"
        data-v5-legislation-target="approval-gate"
        variants={inputConnector(3)}
        d="M356 48C356 70 356 91 356 112"
        fill="none"
        style={{ stroke: AMBER }}
        strokeOpacity={0.5}
        strokeWidth={1.6}
        strokeLinecap="round"
      />
      <motion.path
        data-v5-legislation-connector="corporate-insolvency-governance-act"
        data-v5-legislation-target="approval-gate"
        variants={inputConnector(4)}
        d="M356 232C356 210 356 189 356 168"
        fill="none"
        style={{ stroke: AMBER }}
        strokeOpacity={0.5}
        strokeWidth={1.6}
        strokeLinecap="round"
      />
      <motion.g variants={brandMark} style={SELF_CENTERED}>
        <circle
          data-v5-legislation-terminal="insolvency-act"
          cx={356}
          cy={112}
          r={5}
          fill={PANEL}
          stroke={AMBER}
          strokeWidth={1.4}
        />
        <circle cx={356} cy={112} r={1.8} fill={AMBER} />
      </motion.g>
      <motion.g variants={brandMark} style={SELF_CENTERED}>
        <circle
          data-v5-legislation-terminal="corporate-insolvency-governance-act"
          cx={356}
          cy={168}
          r={5}
          fill={PANEL}
          stroke={AMBER}
          strokeWidth={1.4}
        />
        <circle cx={356} cy={168} r={1.8} fill={AMBER} />
      </motion.g>
    </g>
  );
}

function InvestorOutputCase() {
  return (
    <motion.g
      data-v5-layer="outputs"
      data-v5-role="investor-case-file"
      variants={proofCard(0)}
    >
      <motion.path
        variants={outputDistributor}
        d="M384 140H407"
        fill="none"
        style={{ stroke: BLUE_BRIGHT }}
        strokeWidth={3}
        strokeLinecap="round"
      />
      <path
        data-v5-case-folder=""
        d="M407 81H439L449 91H509V207H407Z"
        style={{ fill: PANEL, stroke: BLUE_BRIGHT }}
        strokeOpacity={0.86}
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
      <text
        x={419}
        y={109}
        fill={BLUE_BRIGHT}
        fontSize={7}
        fontWeight={750}
        letterSpacing="0.16em"
      >
        CASE FILE
      </text>
      <CaseStatusRow y={128} label="APPROVED" />
      <CaseStatusRow y={151} label="AUDIT TRAIL" />
      <CaseStatusRow y={174} label="STATUTORY OUTPUT" compact />
      <path
        d="M419 190H496"
        style={{ stroke: BLUE_BRIGHT }}
        strokeOpacity={0.24}
        strokeWidth={1.2}
        strokeLinecap="round"
      />
      <circle cx={496} cy={193} r={2.8} style={{ fill: BLUE_BRIGHT }} />
    </motion.g>
  );
}

function CaseStatusRow({
  y,
  label,
  compact = false,
}: {
  y: number;
  label: string;
  compact?: boolean;
}) {
  return (
    <g data-v5-case-signal={label.toLowerCase().replace(/\s+/g, '-')}>
      <circle
        cx={421}
        cy={y - 2.5}
        r={5}
        fill={AMBER}
        fillOpacity={0.1}
        stroke={AMBER}
        strokeOpacity={0.72}
      />
      <path
        d={`M418.7 ${y - 2.5}L420.6 ${y - 0.6}L424.1 ${y - 4.6}`}
        fill="none"
        stroke={AMBER}
        strokeWidth={1.3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text
        x={433}
        y={y}
        fill={INK}
        fontSize={compact ? 6.8 : 7.6}
        fontWeight={650}
        letterSpacing="0.08em"
        textLength={compact ? 61 : undefined}
        lengthAdjust={compact ? 'spacingAndGlyphs' : undefined}
      >
        {label}
      </text>
    </g>
  );
}
